import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';

import { TestIdDirective } from '@efficiently/client-cdk-test-id';
import { EffButtonComponent } from '@efficiently/client-ui-kit';
import type { EffInteractiveTone } from '@efficiently/client-ui-types';

import { ClarityDialogRef } from '../dialog/clarity-dialog-ref';
import { ClarityDialogFrameComponent } from '../dialog-frame/clarity-dialog-frame.component';
import type { ClarityOverlayTone } from '../models/clarity-overlay-tone.type';
import { CLARITY_OVERLAY_DATA_TOKEN } from '../tokens/clarity-overlay-data.token';
import type { ClarityActionConfirmDialogData } from './clarity-action-confirm-dialog-data.model';

/** Brain's defaults and its loading label, verbatim (`ConfirmDialog.tsx:48-50, 84`). */
const DEFAULT_CONFIRM_LABEL = 'Confirm';
const DEFAULT_CANCEL_LABEL = 'Cancel';
const DEFAULT_TONE: ClarityOverlayTone = 'danger';
const LOADING_LABEL = 'Processing…';

/** The confirm button's tone follows the dialog's, as brain's primary action does (`ConfirmDialog.tsx:79-86`). */
const CONFIRM_TONE: Readonly<Record<ClarityOverlayTone, EffInteractiveTone>> = {
	danger: 'error',
	neutral: 'accent',
};

/** Ported from Clarity `apps/web/src/shared/ui/dialog/ConfirmDialog.tsx` + `apps/web/src/shared/ui/dialog/DialogChrome.tsx` (commit `f9217bb5a`). */
/**
 * Brain's `ConfirmDialog`: a compact confirmation with loading and an inline error, composed from
 * `ClarityDialogFrameComponent` inside a `passive` `ClarityDialog` — brain's `Modal` (brain defect #36).
 * Opened through `ClarityActionConfirmDialog`, never placed in a template, so the barrel does not export it.
 *
 * Dismissal follows brain's `canDismiss` (`ConfirmDialog.tsx:59-60`): only while the caller allows it
 * **and** nothing is loading. While it is off Cancel is disabled and Escape and the backdrop do nothing,
 * so a half-finished action cannot be walked away from. The frame renders no close control: Cancel
 * closes the dialog; confirming never does — the caller closes it once its action has settled.
 */
@Component({
	selector: 'eff-clarity-action-confirm-dialog',
	imports: [ClarityDialogFrameComponent, EffButtonComponent, TestIdDirective],
	templateUrl: './clarity-action-confirm-dialog.component.html',
	styleUrl: './clarity-action-confirm-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClarityActionConfirmDialogComponent {
	private readonly dialogRef = inject<ClarityDialogRef<void>>(ClarityDialogRef);
	private readonly data = inject<ClarityActionConfirmDialogData>(CLARITY_OVERLAY_DATA_TOKEN);

	protected readonly title = this.data.title;
	protected readonly message = this.data.message;
	protected readonly cancelLabel = this.data.cancelLabel ?? DEFAULT_CANCEL_LABEL;
	protected readonly tone = this.data.tone ?? DEFAULT_TONE;
	protected readonly confirmTone = CONFIRM_TONE[this.tone];

	protected readonly isLoading = computed<boolean>(() => this.data.isLoading?.() ?? false);
	protected readonly canDismiss = computed<boolean>(() => (this.data.isDismissible?.() ?? true) && !this.isLoading());
	protected readonly isConfirmDisabled = computed<boolean>(
		() => this.isLoading() || (this.data.confirmDisabled?.() ?? false),
	);
	protected readonly confirmText = computed<string>(() =>
		this.isLoading() ? LOADING_LABEL : (this.data.confirmLabel ?? DEFAULT_CONFIRM_LABEL),
	);
	/** Brain renders the band for any non-empty error (`DialogChrome.tsx:119-128`); `null` is none. */
	protected readonly error = computed<string | undefined>(() => this.data.error?.() ?? undefined);

	constructor() {
		effect(() => this.dialogRef.setDismissible(this.canDismiss()));
	}

	protected onConfirm(): void {
		this.data.onConfirm();
	}

	protected onClose(): void {
		this.dialogRef.close();
	}
}
